import { inject, Injectable } from '@angular/core';
import { ExtractedDataStoreService } from '../service/extracted-data/extracted-data-store.service';
import { type BatchDomain, CtBatch, CtDocument, WordRect } from '@ps-gui/claims-terminal-shared';
import { TableOverlayStoreService } from '../service/overlay/table-overlay-store.service';
import { BuildSimpleTable, getBuildSimpleExtractedTables } from './build-simple-table-processing';
import { ExtractedLocationStoreService } from '../service/overlay/extracted-location-store.service';
import { associate, entries, mapValues } from '@ps-gui/shared-utils';
import {
  BuildSimpleExtractedFieldDto,
  BuildSimpleExtractionResultDto,
} from '../models/build-simple-model';
import { BuildSimpleExtractedFieldLocations, ExtractedFieldLocations } from '../models/interfaces';
import { ExtractedRow, ExtractedValuesByKey } from '../models/extracted-values';
import { parseValue } from '../utils/type-parsers';
import { getFieldModel, getTableModel } from '../models/extraction-models/extraction-models';
import { TableOverlayService } from '../service/overlay/table-overlay.service';
import { OcrService } from '../service/ocr/ocr.service';
import { fillTableColumnGapsWithIgnoredColumns } from './column-gap-fill-feature';

@Injectable({ providedIn: 'root' })
export class BuildSimpleExtractedDataProcessingService {
  private readonly extractedDataStoreService = inject(ExtractedDataStoreService);
  private readonly tableOverlayStoreService = inject(TableOverlayStoreService);
  private readonly extractedLocationStoreService = inject(ExtractedLocationStoreService);
  private readonly tableOverlayService = inject(TableOverlayService);
  private readonly ocrService = inject(OcrService);

  async storeExtractedSuggestions(
    ctBatch: CtBatch,
    extractionDataByDocumentId: Record<string, BuildSimpleExtractionResultDto | undefined>,
  ): Promise<void> {
    for (const document of ctBatch.documents) {
      const dataForDocument = extractionDataByDocumentId[document.id];
      if (!dataForDocument) continue;

      const extractedBuildSimpleFields = this.getBuildSimpleExtractedFields(
        dataForDocument.entities,
      );

      this.storeExtractedFields(ctBatch.domain, document, extractedBuildSimpleFields);

      const extractedTables = getBuildSimpleExtractedTables(document, dataForDocument.groups);

      fillTableColumnGapsWithIgnoredColumns(extractedTables, this.ocrService);

      await this.storeExtractedTables(ctBatch.domain, document.id, extractedTables);
    }
  }

  private getBuildSimpleExtractedFields(
    extractedFields: BuildSimpleExtractionResultDto['entities'],
  ): Record<string, BuildSimpleExtractedFieldDto> {
    const result: Record<string, BuildSimpleExtractedFieldDto> = {};

    entries(extractedFields).forEach(([fieldKey, fieldDtos]) => {
      fieldDtos.forEach((fieldDto) => {
        const overwrite =
          !(fieldKey in result) || fieldDto.confidence > result[fieldKey].confidence;
        if (overwrite) {
          result[fieldKey] = fieldDto;
        }
      });
    });

    return result;
  }

  private storeExtractedFields(
    domain: BatchDomain,
    document: CtDocument,
    extractedBuildSimpleFields: Record<string, BuildSimpleExtractedFieldDto>,
  ) {
    const extractedFields: ExtractedValuesByKey = mapValues(
      extractedBuildSimpleFields,
      (fieldKey, dto) => parseValue(dto.value, getFieldModel(domain, fieldKey)).value,
    );

    // A field without a location, or with a page number outside this document, cannot be
    // highlighted in the preview. Its value is still stored below.
    const locatedFields = entries(extractedBuildSimpleFields)
      .map(([key, field]) => {
        const pageId = document.pages[field.location?.page - 1]?.id;
        return pageId === undefined ? undefined : { key, location: field.location, pageId };
      })
      .filter((locatedField) => locatedField !== undefined);

    const extractedFieldLocations: BuildSimpleExtractedFieldLocations = associate(
      locatedFields,
      ({ key }) => key,
      ({ location, pageId }) => ({ location, pageId }),
    );

    this.extractedLocationStoreService.setExtractedFieldLocations(
      document.id,
      this.transformFieldLocationsToWordRect(extractedFieldLocations),
    );
    this.extractedDataStoreService.storeExtractedBatchFields(document.id, extractedFields);
  }

  private transformFieldLocationsToWordRect(
    extractedFieldLocations: BuildSimpleExtractedFieldLocations,
  ): ExtractedFieldLocations {
    const obj: ExtractedFieldLocations = {};

    Object.keys(extractedFieldLocations).forEach((key) => {
      const location = extractedFieldLocations[key].location;
      const wordRect: WordRect = {
        x: [location.left, location.right],
        y: [location.upper, location.lower],
      };

      obj[key] = {
        selection: [[wordRect]],
        pageId: extractedFieldLocations[key].pageId,
      };
    });
    return obj;
  }

  private async storeExtractedTables(
    domain: BatchDomain,
    documentId: string,
    buildSimpleTables: BuildSimpleTable[],
  ): Promise<void> {
    this.tableOverlayStoreService.storeTables(buildSimpleTables.map((table) => table.overlayData));

    const rowsByTableKey: Record<string, ExtractedRow[]> = {};

    for (const buildSimpleTable of buildSimpleTables) {
      const tableKey = buildSimpleTable.tableKey;
      const tableModel = getTableModel(domain, tableKey)!;

      const { rows, suggestedFieldLocations } = this.tableOverlayService.createTable(
        buildSimpleTable.overlayData,
        tableModel,
      );

      if (!(tableKey in rowsByTableKey)) {
        rowsByTableKey[tableKey] = [];
      }
      rowsByTableKey[tableKey].push(...rows);

      Object.entries(suggestedFieldLocations).forEach(([fieldId, fieldLocation]) => {
        this.extractedLocationStoreService.replaceExtractedLocation(
          documentId,
          buildSimpleTable.overlayData.pageId,
          fieldId,
          [fieldLocation],
        );
      });
    }

    for (const [tableKey, rows] of Object.entries(rowsByTableKey)) {
      await this.extractedDataStoreService.saveExtractedBatchTablesImmediately(
        documentId,
        tableKey,
        rows,
      );
    }
  }
}
